import { useEffect, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { shouldSkipAnalytics } from '../../analytics/analyticsActor';
import { isStandaloneDisplay } from '../../services/webPush';
import {
  APP_INSTALLS_COLLECTION,
  appInstallAlreadyRecorded,
  installPlatform,
  markPendingInstallSource,
  pendingSourceWhileSignedOut,
  readPendingInstallSource,
  recordAppInstallOnce,
  resolveAppInstallSource,
} from './recordAppInstall';

/** Record a signed-in install once per account. Guests keep a pending flag. */
export default function useRecordAppInstall() {
  const { currentUser } = useAuth();
  const uid = currentUser && !currentUser.isAnonymous ? currentUser.uid : null;
  const [appInstalled, setAppInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onInstalled = () => setAppInstalled(true);
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (shouldSkipAnalytics()) return undefined;
    const storage = window.localStorage;
    const standalone = isStandaloneDisplay();
    if (!uid) {
      const pending = pendingSourceWhileSignedOut({ standalone, appInstalled });
      if (pending) markPendingInstallSource(pending, storage);
      return undefined;
    }
    const source = resolveAppInstallSource({
      uid,
      alreadyRecorded: appInstallAlreadyRecorded(uid, storage),
      standalone,
      appInstalled,
      pendingSource: readPendingInstallSource(storage),
    });
    if (!source) return undefined;
    recordAppInstallOnce({
      uid,
      source,
      platform: installPlatform(window.navigator),
      storage,
      installedAt: serverTimestamp(),
      readDoc: async (id) => {
        const snap = await getDoc(doc(db, APP_INSTALLS_COLLECTION, id));
        return snap.exists();
      },
      createDoc: async (id, data) => {
        await setDoc(doc(db, APP_INSTALLS_COLLECTION, id), data);
      },
    });
    return undefined;
  }, [uid, appInstalled]);
}
